import axios, { type AxiosError } from "axios";
import { API_URLS } from "@/config/urls";
import { LANG_EN, LANG_JP } from "@/constants";
import { api } from "@/lib/axios";
import type { User } from "@/lib/humhub-api/types";
import i18n from "@/lib/i18n";

function isAxiosError(error: unknown): error is AxiosError {
  return axios.isAxiosError(error);
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  code: number;
  message: string;
  auth_token: string;
  expired_at: string;
}

export const authApi = {
  getCurrentUser: async (): Promise<User> => {
    try {
      const response = await api.get<User>(
        `${API_URLS.AUTH}/current-with-banner`,
      );
      return response.data;
    } catch (error: unknown) {
      if (isAxiosError(error) && !error.response) {
        throw new Error("Please check your connection");
      }
      throw new Error(
        `Failed to get user info => ${
          isAxiosError(error)
            ? (error.response?.data as { message?: string })?.message ||
              "Unknown error"
            : error.message
        }`,
      );
    }
  },
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    try {
      const response = await api.post<LoginResponse>(
        `${API_URLS.AUTH}/login`,
        credentials,
      );
      const data = response.data;
      if (data.code === 200 && data.auth_token) {
        return {
          code: data.code,
          message: data.message,
          auth_token: data.auth_token,
          expired_at: data.expired_at,
        };
      }
      throw new Error(data.message || "Login failed");
    } catch (error: unknown) {
      if (isAxiosError(error) && !error.response) {
        throw new Error("Please check your connection");
      }

      const errorResponse = isAxiosError(error) ? error.response?.data : error;
      throw new Error(errorResponse?.message || "Failed to Login");
    }
  },
  oidcLogin: async (accessToken: string): Promise<LoginResponse> => {
    if (!accessToken) {
      console.error("oidcLogin called with empty access token");
      throw new Error("アクセストークンが指定されていません");
    }

    try {
      const rawLocale =
        (i18n as unknown as { locale?: string }).locale ?? i18n.language;
      const language = rawLocale?.startsWith(LANG_JP) ? LANG_JP : LANG_EN;

      const response = await api.post<LoginResponse>(
        `${API_URLS.AUTH}/oidc-login`,
        { language },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (!response.data.auth_token) {
        console.error(
          "HumHub OIDC login API returned no auth_token",
          response.data,
        );
        throw new Error("認証トークンが返されませんでした");
      }

      return response.data;
    } catch (error: unknown) {
      // Axiosエラーかどうかをチェック
      if (axios.isAxiosError(error)) {
        console.error("HumHub OIDC login API error", {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
        });

        // エラーレスポンスがある場合はそれを返す
        if (error.response?.data) {
          throw new Error(
            error.response.data.message || "HumHub OIDC login failed",
          );
        }
      } else {
        console.error("HumHub OIDC login unknown error", {
          message: error instanceof Error ? error.message : "Unknown error",
        });
      }

      // エラーレスポンスがない場合はエラーをスロー
      throw new Error(error instanceof Error ? error.message : String(error));
    }
  },
  oidcLogout: async () => {
    try {
      const response = await api.post<LoginResponse>(
        `${API_URLS.AUTH}/oidc-logout`,
        {}, // empty body
        {
          withCredentials: true,
        },
      );
      return response.data;
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        console.error("HumHub OIDC logout API error", {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
        });

        // エラーレスポンスがある場合はそれを返す
        if (error.response?.data) {
          throw new Error(
            error.response.data.message || "HumHub OIDC logout failed",
          );
        }
      } else {
        console.error("HumHub OIDC logout unknown error", {
          message: error instanceof Error ? error.message : "Unknown error",
        });
      }

      // エラーレスポンスがない場合はエラーをスロー
      throw new Error(error instanceof Error ? error.message : String(error));
    }
  },
  testLogin: async (credentials: LoginRequest): Promise<LoginResponse> => {
    // Prevent test login in production environment
    if (!import.meta.env.DEV) {
      console.error("Test login is not available in production environment");
      throw new Error("テストログインは本番環境では利用できません");
    }

    try {
      const response = await api.post<LoginResponse>(
        `${API_URLS.AUTH}/login`,
        credentials,
      );
      const data = response.data;
      if (data.code === 200 && data.auth_token) {
        return {
          code: data.code,
          message: data.message,
          auth_token: data.auth_token,
          expired_at: data.expired_at,
        };
      }
      throw new Error(data.message || "Test login failed");
    } catch (error: unknown) {
      if (isAxiosError(error) && !error.response) {
        throw new Error("Please check your connection");
      }

      const errorResponse = isAxiosError(error) ? error.response?.data : error;
      throw new Error(errorResponse?.message || "Failed to test login");
    }
  },
};
